import React, { useEffect, useRef, useState } from "react";
import { Box, Button, IconButton, Tooltip, useTheme } from "@mui/material";
import {
  Check,
  CheckCircle,
  Clock,
  Link2,
  PenLine,
  Pin,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";
import { FireMmkIcon } from "../../assets/icon";
import { CustomTypography } from "../custom-typography/custom-typography";
import { MmkItem } from "../../types/mmk";
import { isPersonalMmkItem } from "../../helpers/mmkPersonal";
import { getMmkCardDisplayLabel } from "../../helpers/mmkDisplayLabel";
import { isValidSourceUrl } from "../../utils/mmkUrl";
import {
  MmkCardBackContent,
  MmkCardFrontContent,
} from "./mmk-card-face-content";
import {
  getMmkCardHeadingStyles,
  mmkCardHeaderStyles,
  mmkCardHeaderLeftStyles,
  getMmkCardStyles,
  getMmkPinButtonStyles,
  getMmkReviewLaterButtonStyles,
  getMmkSelectButtonStyles,
  getMmkIKnowItButtonStyles,
  mmkActionsContainerStyles,
  getMmkCardVariantFromItem,
} from "./styles";

// Shared height for Deck / Focus Session cards.
export const MMK_FULL_LENGTH_MIN_HEIGHT = 300;
const MMK_GRID_MIN_HEIGHT = 220;

// Classic always-on scrollbar looks bad on cards; overlay scrollbars elsewhere are fine.
function isWindowsChrome(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isWindows = /Windows/i.test(ua);
  const isChrome =
    /Chrome/i.test(ua) &&
    !/Edg\//i.test(ua) &&
    !/OPR\//i.test(ua) &&
    !/SamsungBrowser/i.test(ua);
  return isWindows && isChrome;
}

interface MmkCardProps {
  item: MmkItem;
  isActive: boolean;
  isFlipped: boolean;
  isSelectionMode: boolean;
  isSelected: boolean;
  sectionKey?: "pinned" | "to_review" | "known";
  forceDefaultVariant?: boolean;
  fullLength?: boolean;
  onToggleSelect: () => void;
  onFlip: () => void;
  onClose: () => void;
  onPin: () => void;
  onReviewLater: () => void;
  onKnowIt: () => void;
  onEditPersonal?: () => void;
  onDeletePersonal?: () => void;
}

export const MmkCard: React.FC<MmkCardProps> = ({
  item,
  isActive,
  isFlipped,
  isSelectionMode,
  isSelected,
  sectionKey,
  forceDefaultVariant = false,
  fullLength = false,
  onToggleSelect,
  onFlip,
  onClose,
  onPin,
  onReviewLater,
  onKnowIt,
  onEditPersonal,
  onDeletePersonal,
}) => {
  const theme = useTheme();
  const accentTimerRef = useRef<number | null>(null);
  const [isAccentAnimated, setIsAccentAnimated] = useState(false);
  const question = item.question || item.title;
  const answer = item.approved_answer || item.content;
  const sourceRef = isValidSourceUrl(item.source_ref) ? item.source_ref : null;
  const headerMkkLabel = getMmkCardDisplayLabel(item);
  const cardVariant =
    forceDefaultVariant || sectionKey === "to_review"
      ? "default"
      : getMmkCardVariantFromItem(item);
  const isReviewChecked = !!item.is_review_later;
  const isKnowItChecked = !!item.is_mastered;
  const cardMinHeight = fullLength
    ? MMK_FULL_LENGTH_MIN_HEIGHT
    : MMK_GRID_MIN_HEIGHT;
  const hideClassicScrollbar = isWindowsChrome();

  useEffect(() => {
    return () => {
      if (accentTimerRef.current) {
        window.clearTimeout(accentTimerRef.current);
      }
    };
  }, []);

  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsAccentAnimated(true);
    if (accentTimerRef.current) {
      window.clearTimeout(accentTimerRef.current);
    }
    accentTimerRef.current = window.setTimeout(() => {
      setIsAccentAnimated(false);
      accentTimerRef.current = null;
    }, 450);
    if (isSelectionMode) {
      onToggleSelect();
    } else {
      onFlip();
    }
  };

  const personalActions = !isSelectionMode &&
    isPersonalMmkItem(item) &&
    (onEditPersonal || onDeletePersonal) && (
      <>
        {onEditPersonal && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              aria-label="Edit personal card"
              onClick={(e) => {
                e.stopPropagation();
                onEditPersonal();
              }}
            >
              <PenLine size={16} />
            </IconButton>
          </Tooltip>
        )}
        {onDeletePersonal && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              aria-label="Delete personal card"
              onClick={(e) => {
                e.stopPropagation();
                onDeletePersonal();
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        )}
      </>
    );

  const selectControl = isSelectionMode && (
    <Box
      sx={getMmkSelectButtonStyles(isSelected)}
      onClick={(e) => {
        e.stopPropagation();
        onToggleSelect();
      }}
    >
      {isSelected && <Check size={14} color="white" strokeWidth={3} />}
    </Box>
  );

  const cardHeader = (
    <Box sx={mmkCardHeaderStyles}>
      <Box sx={mmkCardHeaderLeftStyles}>
        <FireMmkIcon style={{ width: 16, height: 16 }} />
        <CustomTypography
          fontWeight={600}
          fontSize="14px"
          sx={getMmkCardHeadingStyles()}
        >
          {headerMkkLabel}
        </CustomTypography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {personalActions}
        {selectControl}
      </Box>
    </Box>
  );

  const faceShellSx = {
    position: "absolute" as const,
    inset: 0,
    backfaceVisibility: "hidden" as const,
    WebkitBackfaceVisibility: "hidden" as const,
    // Keep accent ::before on this non-scrolling shell so it stays full card height.
    overflow: "hidden" as const,
    p: 0,
  };

  const faceScrollSx = {
    height: "100%",
    overflow: "auto" as const,
    p: 2.5,
    boxSizing: "border-box" as const,
    display: "flex" as const,
    flexDirection: "column" as const,
    ...(hideClassicScrollbar
      ? {
          scrollbarWidth: "none" as const,
          msOverflowStyle: "none" as const,
          "&::-webkit-scrollbar": { display: "none" },
        }
      : null),
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: cardMinHeight,
        height: cardMinHeight,
        perspective: "1000px",
        borderRadius: "16px",
      }}
      onClick={handleCardClick}
    >
      <motion.div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: cardMinHeight,
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
      >
        <Box
          sx={{
            ...((
              getMmkCardStyles(
                false,
                false,
                false,
                cardVariant,
                false,
                isAccentAnimated,
              ) as (t: unknown) => object
            )(theme) as object),
            ...faceShellSx,
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
          }}
        >
          <Box sx={faceScrollSx}>
            {cardHeader}
            <MmkCardFrontContent question={question} variant={cardVariant} />
          </Box>
        </Box>

        <Box
          sx={{
            ...((
              getMmkCardStyles(
                false,
                false,
                false,
                cardVariant,
                true,
                isAccentAnimated,
              ) as (t: unknown) => object
            )(theme) as object),
            ...faceShellSx,
            transform: "rotateY(180deg) translateZ(0)",
            WebkitTransform: "rotateY(180deg) translateZ(0)",
          }}
        >
          <Box sx={{ ...faceScrollSx, justifyContent: "flex-start" }}>
            {cardHeader}
            <MmkCardBackContent
              question={question}
              answer={answer}
              shortAnswer={item.short_answer}
              variant={cardVariant}
            />
            {sourceRef && (
              <Button
                size="small"
                startIcon={<Link2 size={14} />}
                sx={{ textTransform: "none", mb: 1.5, px: 1, minWidth: "auto" }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(sourceRef, "_blank", "noopener,noreferrer");
                }}
              >
                Source
              </Button>
            )}
            <Box sx={mmkActionsContainerStyles}>
              <Button
                size="small"
                disabled={sectionKey === "pinned"}
                startIcon={<Pin size={16} />}
                sx={getMmkPinButtonStyles(item.is_pinned)}
                onClick={(e) => {
                  e.stopPropagation();
                  onPin();
                }}
              >
                {item.is_pinned ? "PIN ✓" : "PIN"}
              </Button>
              <Button
                size="small"
                disabled={sectionKey === "to_review"}
                startIcon={<Clock size={16} />}
                sx={getMmkReviewLaterButtonStyles(isReviewChecked)}
                onClick={(e) => {
                  e.stopPropagation();
                  onReviewLater();
                }}
              >
                {isReviewChecked ? "Review ✓" : "Review"}
              </Button>
              <Button
                size="small"
                disabled={sectionKey === "known"}
                startIcon={<CheckCircle size={16} color="#FFF" />}
                sx={getMmkIKnowItButtonStyles(isKnowItChecked)}
                onClick={(e) => {
                  e.stopPropagation();
                  onKnowIt();
                }}
              >
                {isKnowItChecked ? "I Know It ✓" : "I Know It"}
              </Button>
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};
